import React from "react";
import SortableTable from "../SortableTable";
import NoRecordsAvailable from "../NoRecordsAvailable";
import { useProjectDesignationOptions } from "../../Hooks/useProjectDesignationOptions";
import { displayFormatDate } from "../../utils/helpers";
import AddMembers from "./AddMembers";
import { useState, useEffect } from "react";
import { EditMembers } from "./EditMembers";
import { toast } from "react-toastify";
import axios from "axios";
import Cookies from "js-cookie";
import { ThreeDots } from "react-loader-spinner";
import { StatusEnum } from "../../constants/employeeStatusEnum";
import {
  API_PROJECT_EXPERTIES,
  API_SHADOW_RESOURCES,
} from "../../config/Endpoints";
import { useShadowEmployeeStore } from "../../stores/shadowEmployeeListStore";
import { useMainEmployeeStore } from "../../stores/mainEmployeeListStore";
import { useTeamMembersListByProjectIDStore } from "../../stores/teamMembersListByProjectIDStore";

const ShadowTeamMembersTable = ({ project }) => {
  const { options: designationOptions } = useProjectDesignationOptions();
  const [selectedMember, setSelectedMember] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const { shadowEmployeeList, setShadowEmployeeList, fetchShadowEmployees } =
    useShadowEmployeeStore();
  const { mainEmployeeList, fetchMainEmployees } = useMainEmployeeStore();
  const { teamMembersList, fetchTeamMembers } =
    useTeamMembersListByProjectIDStore();
  const [shadowResources, setShadowResources] = useState(
    project.shadowResources || []
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [expertiseOptions, setExpertiseOptions] = useState([]);
  const [isExpertiseLoading, setIsExpertiseLoading] = useState(false);
  const accessToken = Cookies.get("accessToken");
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsLoadingDelete(true);
    try {
      const res = await axios.delete(
        `${API_SHADOW_RESOURCES}/${deleteTarget}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      await fetchShadowEmployees(project.projectId);
      setShadowResources((prev) => prev.filter((m) => m.id !== deleteTarget));
      setShadowEmployeeList(
        shadowEmployeeList.filter((m) => m.id !== deleteTarget)
      );
      setShowDeleteModal(false);
      setDeleteTarget(null);
      toast.success(res?.data?.message || "Member deleted successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete member");
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setShowEditModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedMember(null);
    document.body.style.overflow = "";
  };

  const addMemberToTable = (newMember) => {
    setShadowResources((prev) => [...prev, newMember]);
    setShadowEmployeeList(
      shadowEmployeeList.filter((e) => e.value !== newMember.shadowEmployeeId)
    );
  };

  const updateMemberInTable = (updatedMember) => {
    setShadowResources((prev) =>
      prev.map((m) => (m.id === updatedMember.id ? updatedMember : m))
    );
  };
  const columns = [
    {
      key: "shadowEmployee",
      label: "Shadow Employee",
      render: (item) => item.shadowEmployeeName,
    },
    {
      key: "mainEmployee",
      label: "Main Employee",
      render: (item) => item.workingEmployeeName,
    },
    {
      key: "designation",
      label: "Shadow Resource Designation",
      render: (item) => {
        const option = designationOptions.find(
          (opt) => opt.value === item.designation
        );
        return option ? option.label : "";
      },
    },
    {
      key: "expertise",
      label: "Project Expertise",
      render: (item) => item.expertise?.trim() || "-",
    },
    {
      key: "joiningDate",
      label: "Joining Date",
      compare: (a, b) => new Date(a.joiningDate) - new Date(b.joiningDate),
      render: (item) => displayFormatDate(item.joiningDate),
    },
    {
      key: "endDate",
      label: "End Date",
      compare: (a, b) => new Date(a.endDate) - new Date(b.endDate),
      render: (item) => displayFormatDate(item.endDate),
    },
    {
      key: "status",
      label: "Status",
      render: (item) => {
        switch (item.status) {
          case 1:
            return "Active";
          case 0:
            return "Inactive";
          case 2:
            return "Deallocated";
          default:
            return item.status || "-";
        }
      },
    },
    {
      key: "action",
      label: "Action",
      render: (item) => (
        <div>
          <a
            href="javascript:void(0)"
            className={
              item.status === StatusEnum.DEALLOCATED
                ? "fw-bolder text-muted"
                : "fw-bolder"
            }
            onClick={() => {
              if (item.status !== StatusEnum.DEALLOCATED) {
                handleEditMember(item);
              }
            }}
            style={{
              cursor:
                item.status === StatusEnum.DEALLOCATED
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            Edit
          </a>

          <a
            href="#"
            className="ms-3 text-danger fw-bolder"
            onClick={(e) => {
              e.preventDefault();
              setDeleteTarget(item.id);
              setShowDeleteModal(true);
            }}
          >
            Delete
          </a>
        </div>
      ),
      sortable: false,
    },
  ];
  useEffect(() => {
    if (project?.projectId) {
      fetchShadowEmployees(project.projectId);
      fetchMainEmployees(project.projectId);
      fetchTeamMembers(project.projectId);
    }
  }, [project?.projectId]);

  useEffect(() => {
    let active = true;
    setIsExpertiseLoading(true);
    axios
      .get(API_PROJECT_EXPERTIES, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => {
        if (!active) return;
        const values = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        setExpertiseOptions(
          values.map((value) => ({
            value: String(value),
            label: String(value),
          })),
        );
      })
      .catch((error) => {
        if (!active) return;
        console.error("Failed to load project expertise:", error);
        setExpertiseOptions([]);
        toast.error("Failed to load project expertise list");
      })
      .finally(() => {
        if (active) setIsExpertiseLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accessToken]);
  return (
    <div className="col-md-12 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5
          className="mb-0"
          style={{
            fontSize: "16px",
            fontWeight: "500",
            borderBottom: "none",
            borderRadius: "2px",
            padding: "2px 10px",
            backgroundColor: "white",
          }}
        >
          Shadow Members
        </h5>
        <AddMembers
          project={project}
          shadowEmployeeList={shadowEmployeeList}
          mainEmployeeList={mainEmployeeList}
          onMemberAdded={addMemberToTable}
          teamMembersList={teamMembersList}
          expertiseOptions={expertiseOptions}
          isExpertiseLoading={isExpertiseLoading}
        />
      </div>
      {shadowResources && shadowResources.length > 0 ? (
        <SortableTable columns={columns} data={shadowResources} />
      ) : (
        <NoRecordsAvailable />
      )}

      {showDeleteModal && (
        <>
          {/* Backdrop */}
          <div className="modal-backdrop fade show"></div>

          {/* Modal */}
          <div className="modal d-block" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-md">
              <div className="modal-content">
                <div className="modal-body">
                  <h5 className="text-center">
                    Are you sure you want to delete this member?
                  </h5>
                </div>
                <div className="justify-content-center modal-footer">
                  <button
                    type="button"
                    className={
                      isLoadingDelete ? "theme-button disabled" : "theme-button"
                    }
                    onClick={handleConfirmDelete}
                  >
                    {isLoadingDelete ? (
                      <ThreeDots
                        height="20"
                        width="53"
                        radius="9"
                        color="white"
                        ariaLabel="three-dots-loading"
                        visible={true}
                      />
                    ) : (
                      "Yes"
                    )}
                  </button>
                  <button
                    type="button"
                    className="theme-button bg-grey mx-3 w-35"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeleteTarget(null);
                    }}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showEditModal && (
        <EditMembers
          project={project}
          memberData={selectedMember}
          closeModal={closeEditModal}
          openModal={showEditModal}
          shadowEmployeeList={shadowEmployeeList}
          mainEmployeeList={mainEmployeeList}
          expertiseOptions={expertiseOptions}
          isExpertiseLoading={isExpertiseLoading}
          onMemberUpdate={updateMemberInTable}
        />
      )}
    </div>
  );
};

export default ShadowTeamMembersTable;
